/**
 * Where a parent entered the one-invoice checkout from, carried onto its payment events so
 * adoption of paying several invoices at once can be measured: did a family with the cart
 * choose "Pay only this", or pay from the invoice list as before?
 */
export type CheckoutSource = 'pay_only_this' | 'invoice_list' | 'invoice_detail' | 'due_now';

// due_now: the Due now banner or the cart when only one invoice is due -- one invoice is never a bundle.
const SOURCES: readonly CheckoutSource[] = ['pay_only_this', 'invoice_list', 'invoice_detail', 'due_now'];
const storageKey = (invoiceId: string) => `bhcs_checkout_source:${invoiceId}`;

/** The checkout link for one invoice, tagged with where it was opened from. */
export function checkoutPath(invoiceId: string, source: CheckoutSource): string {
  return `/payment-checkout/${invoiceId}?source=${source}`;
}

/**
 * The source for this checkout. The URL wins and is remembered for the tab, because the bank
 * account setup leaves the page and returns without it. Unknown values count as none.
 */
export function checkoutSourceFor(invoiceId: string, params: URLSearchParams): CheckoutSource | null {
  const fromUrl = params.get('source');
  if (fromUrl && (SOURCES as readonly string[]).includes(fromUrl)) {
    try {
      sessionStorage.setItem(storageKey(invoiceId), fromUrl);
    } catch {
      // storage unavailable: the URL value still applies to this page view
    }
    return fromUrl as CheckoutSource;
  }
  try {
    const stored = sessionStorage.getItem(storageKey(invoiceId));
    return stored && (SOURCES as readonly string[]).includes(stored) ? (stored as CheckoutSource) : null;
  } catch {
    return null;
  }
}
